import { extInSite } from './IvrShared';
import { useEffect, useState } from 'react';
import api from '../services/ApiService';
import axios from 'axios';
import { useCustomer } from './customerCtx';
import { ZoomTable, ZoomBtn, ZoomModal, ZoomField } from './zoom/ZoomTable';

export default function ExtensionsPage({ showTenantSelector, zoom, site }: { showTenantSelector?: boolean; zoom?: boolean; site?: any }) {
  const xcust = useCustomer();
  const [extensions, setExtensions] = useState<any[]>([]);
  const [users, setUsers]           = useState<any[]>([]);
  const [tenants, setTenants]       = useState<any[]>([]);
  const [trunks, setTrunks]         = useState<any[]>([]);
  const [extensionNumber, setExtensionNumber] = useState('');
  const [displayName, setDisplayName]         = useState('');
  const [userId, setUserId]                   = useState('');
  const [tenantId, setTenantId]               = useState('');
  const [filterTenant, setFilterTenant]       = useState('');
  const [showAddExt, setShowAddExt]           = useState(false);
  const [extSearch, setExtSearch]             = useState('');
  // Queue numbers (2026-10-28): an internal number that rings a queue (e.g. 2555 → AST L24 RECEPTION)
  const [qn, setQn]       = useState<any>({ enabled: false, queues: [] });
  const [dest, setDest]   = useState('user');
  const [qId, setQId]     = useState('');
  const loadQn = () => axios.get('/api/queues/internal-numbers').then((r) => setQn(r.data || { enabled: false, queues: [] })).catch(() => {});
  const setQueueNumber = async (id: any, number: string) => {
    try { await axios.put(`/api/queues/${id}/internal-number`, { number }); await loadQn(); return true; }
    catch (e: any) { alert(e?.response?.data?.error || 'Could not save the queue number'); return false; }
  };

  const loadData = async () => {
    const calls: Promise<any>[] = [api.getExtensions(), api.getAdminUsers(), (api as any).getTrunks()];
    if (showTenantSelector) calls.push((api as any).getAsteriskTenants());
    // One refused/failed request (e.g. trunks for a restricted admin) must not blank the page
    const [extData, userData, trunkData, tenantData] = await Promise.all(calls.map((c) => c.catch(() => null)));
    setExtensions(extData || []);
    setUsers(userData || []);
    setTrunks(trunkData || []);
    if (showTenantSelector && tenantData) {
      setTenants(tenantData);
      if (tenantData.length > 0 && !tenantId) setTenantId(String(tenantData[0].id));
    }
  };

  useEffect(() => { loadData(); loadQn(); }, []); // eslint-disable-line

  const createExtension = async () => {
    if (!extensionNumber.trim()) {
      alert('Extension number required');
      return;
    }

    await api.createExtension({
      extension_number: extensionNumber.trim(),
      display_name: displayName.trim() || extensionNumber.trim(),
      user_id: userId ? Number(userId) : null,
      tenant_id: tenantId ? Number(tenantId) : undefined,
      webrtc_enabled: true,
      internal_call_enabled: true,
      external_call_enabled: true,
      voicemail_enabled: false,
      recording_enabled: false,
      is_active: true
    });

    setExtensionNumber('');
    setDisplayName('');
    setUserId('');
    await loadData();
  };

  const tenantName = (id: any) => tenants.find(t => t.id === Number(id))?.tenant_name || `Tenant ${id}`;

  const visibleExtensions = (filterTenant
    ? extensions.filter(e => String(e.tenant_id) === filterTenant)
    : extensions)
    .filter((e: any) => extInSite(e.extension_number, site))
    .filter((e: any) => xcust.match('extension', e.id));

  const updateExtension = async (e: any, changes: any) => {
    const updated = { ...e, ...changes };

    await api.updateExtension(e.id, {
      user_id: updated.user_id || null,
      extension_number: updated.extension_number,
      display_name: updated.display_name,
      sip_username: updated.sip_username,
      sip_password: updated.sip_password,
      webrtc_enabled: !!updated.webrtc_enabled,
      internal_call_enabled: !!updated.internal_call_enabled,
      external_call_enabled: !!updated.external_call_enabled,
      voicemail_enabled: !!updated.voicemail_enabled,
      recording_enabled: !!updated.recording_enabled,
      is_active: !!updated.is_active,
      outbound_trunk_id: updated.outbound_trunk_id ?? null,
      outbound_limit_minutes: updated.outbound_limit_minutes ?? null
    });

    await loadData();
  };

  const deleteExtension = async (e: any) => {
    if (!window.confirm(`Delete extension ${e.extension_number}?`)) return;
    await api.deleteExtension(e.id);
    await loadData();
  };

  // ── Zoom-console variant (TEC tenant) — reuses all the same state/handlers ──
  if (zoom) {
    const doCreate = async () => {
      if (dest === 'queue') {
        if (!qId) return alert('Choose the queue');
        if (!extensionNumber.trim()) return alert('Enter the number');
        if (await setQueueNumber(qId, extensionNumber.trim())) { setExtensionNumber(''); setQId(''); setDest('user'); setShowAddExt(false); }
        return;
      }
      await createExtension(); setShowAddExt(false);
    };
    const pickQueue = (id: string) => {
      setQId(id);
      const q = (qn.queues || []).find((x: any) => String(x.id) === id);
      if (q && !extensionNumber.trim()) setExtensionNumber(q.internal_number || String(q.did || '').replace(/\D/g, '').slice(-4));
    };
    const qRows = (qn.queues || []).filter((q: any) => q.internal_number);
    const rows = visibleExtensions.filter((e: any) =>
      !extSearch.trim() || `${e.extension_number} ${e.display_name || ''}`.toLowerCase().includes(extSearch.toLowerCase()));
    const TogglePill = ({ on, onClick, label }: any) => (
      <button className={`z-badge ${on ? 'z-badge-green' : 'z-badge-gray'}`} style={{ border: 'none', cursor: 'pointer' }} onClick={onClick}>{label}</button>
    );

    const columns: any[] = [
      { key: 'ext', label: 'Extension', render: (e: any) => (
        <div>
          <div className="z-tbl-primary-cell">{e.extension_number}</div>
          <input className="z-inline-input" style={{ width: 150, marginTop: 4 }} value={e.display_name || ''} placeholder="Display name"
            onChange={(ev) => updateExtension(e, { display_name: ev.target.value })} />
        </div>
      ) },
      { key: 'user', label: 'Assigned User', render: (e: any) => (
        <select className="z-inline-select" value={e.user_id || ''} onChange={(ev) => updateExtension(e, { user_id: ev.target.value ? Number(ev.target.value) : null })}>
          <option value="">Unassigned</option>
          {users.map((u: any) => <option key={u.id} value={u.id}>{u.username}</option>)}
        </select>
      ) },
      { key: 'trunk', label: 'Outbound Trunk', render: (e: any) => (
        <select className="z-inline-select" value={e.outbound_trunk_id || ''} onChange={(ev) => updateExtension(e, { outbound_trunk_id: ev.target.value ? Number(ev.target.value) : null })}>
          <option value="">tenant default</option>
          {trunks.map((t: any) => <option key={t.id} value={t.id}>{t.display_name || t.trunk_name}</option>)}
        </select>
      ) },
      { key: 'limit', label: 'Out. Limit', render: (e: any) => (
        <input className="z-inline-input" style={{ width: 70 }} type="number" min={0} placeholder="—" value={e.outbound_limit_minutes ?? ''}
          onChange={(ev) => updateExtension(e, { outbound_limit_minutes: ev.target.value === '' ? null : Number(ev.target.value) })} />
      ) },
      { key: 'calling', label: 'Calling', render: (e: any) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <TogglePill on={!!e.webrtc_enabled} label="Web" onClick={() => updateExtension(e, { webrtc_enabled: e.webrtc_enabled ? 0 : 1 })} />
          <TogglePill on={!!e.internal_call_enabled} label="Int" onClick={() => updateExtension(e, { internal_call_enabled: e.internal_call_enabled ? 0 : 1 })} />
          <TogglePill on={!!e.external_call_enabled} label="Ext" onClick={() => updateExtension(e, { external_call_enabled: e.external_call_enabled ? 0 : 1 })} />
        </div>
      ) },
      { key: 'status', label: 'Status', render: (e: any) => (
        <button className={`z-badge ${e.is_active ? 'z-badge-green' : 'z-badge-red'}`} style={{ border: 'none', cursor: 'pointer' }}
          onClick={() => updateExtension(e, { is_active: e.is_active ? 0 : 1 })}>{e.is_active ? 'Active' : 'Disabled'}</button>
      ) },
    ];
    if (showTenantSelector) columns.splice(1, 0, { key: 'tenant', label: 'Tenant', render: (e: any) => tenantName(e.tenant_id) });

    return (
      <>
        {showAddExt && (
          <ZoomModal title="Add Extension" onClose={() => setShowAddExt(false)}
            footer={<><ZoomBtn onClick={() => setShowAddExt(false)}>Cancel</ZoomBtn><ZoomBtn variant="primary" onClick={doCreate}>Add</ZoomBtn></>}>
            {showTenantSelector && tenants.length > 0 && (
              <ZoomField label="Tenant">
                <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                  {tenants.map((t: any) => <option key={t.id} value={t.id}>{t.tenant_name}</option>)}
                </select>
              </ZoomField>
            )}
            {qn.enabled && (
              <ZoomField label="Destination" hint="A queue number rings the queue when dialled internally (its office hours and holidays apply)">
                <select value={dest} onChange={(e) => setDest(e.target.value)}>
                  <option value="user">A person (phone extension)</option>
                  <option value="queue">A queue</option>
                </select>
              </ZoomField>
            )}
            {dest === 'queue' && qn.enabled ? (
              <div className="z-field-grid">
                <ZoomField label="Queue"><select value={qId} onChange={(e) => pickQueue(e.target.value)}>
                  <option value="">— choose a queue —</option>
                  {(qn.queues || []).map((q: any) => <option key={q.id} value={q.id}>{q.display_name || q.queue_name}{q.internal_number ? ` (now ${q.internal_number})` : ''}</option>)}</select></ZoomField>
                <ZoomField label="Number" hint="Suggested: last 4 digits of the queue's direct number"><input value={extensionNumber} onChange={(e) => setExtensionNumber(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="e.g. 2555" /></ZoomField>
              </div>
            ) : (<>
            <div className="z-field-grid">
              <ZoomField label="Extension Number"><input value={extensionNumber} onChange={(e) => setExtensionNumber(e.target.value)} placeholder="e.g. 1001" /></ZoomField>
              <ZoomField label="Display Name"><input value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></ZoomField>
            </div>
            <ZoomField label="Assign to User">
              <select value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">No user assigned</option>
                {users.filter((u: any) => !showTenantSelector || !tenantId || String(u.tenant_id) === tenantId).map((u: any) => (
                  <option key={u.id} value={u.id}>{u.username}</option>
                ))}
              </select>
            </ZoomField>
            </>)}
          </ZoomModal>
        )}

        <ZoomTable
          title="Extensions"
          subtitle="Manage tenant extensions and assign them to users."
          search={extSearch}
          onSearch={setExtSearch}
          searchPlaceholder="Search extensions"
          actions={<ZoomBtn variant="primary" onClick={() => setShowAddExt(true)}>+ Add Extension</ZoomBtn>}
          columns={columns}
          rows={rows}
          rowKey={(e: any) => e.id}
          rowActions={(e: any) => <ZoomBtn variant="danger" onClick={() => deleteExtension(e)}>Delete</ZoomBtn>}
          emptyText="No extensions yet"
        />
        {qn.enabled && (
          <div style={{ marginTop: 18 }}>
            <ZoomTable
              title="Queue numbers"
              subtitle="Internal numbers that ring a queue. Add one with + Add Extension → Destination: A queue."
              columns={[
                { key: 'n', label: 'Number', render: (q: any) => <b>{q.internal_number}</b> },
                { key: 'q', label: 'Rings queue', render: (q: any) => q.display_name || q.queue_name },
                { key: 'd', label: 'Queue direct number', render: (q: any) => q.did || '—' },
              ]}
              rows={qRows}
              rowKey={(q: any) => q.id}
              selectable={false}
              rowActions={(q: any) => <ZoomBtn variant="danger" onClick={() => { if (window.confirm(`Remove number ${q.internal_number} from ${q.display_name || q.queue_name}?`)) setQueueNumber(q.id, ''); }}>Remove</ZoomBtn>}
              emptyText="No queue numbers yet"
            />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="admin-page-title">Extensions</div>
      <div className="admin-page-subtitle">
        Manage tenant extensions and assign them to users.
      </div>

      {/* Tenant filter bar — only for ast_admin */}
      {showTenantSelector && tenants.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>Tenant filter:</span>
          <button
            onClick={() => setFilterTenant('')}
            style={{
              padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', border: 'none',
              background: !filterTenant ? '#8b5cf6' : 'rgba(100,116,139,0.2)',
              color: !filterTenant ? '#fff' : '#94a3b8', fontWeight: !filterTenant ? 700 : 400,
            }}>All</button>
          {tenants.map((t: any) => (
            <button key={t.id} onClick={() => setFilterTenant(String(t.id))} style={{
              padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', border: 'none',
              background: filterTenant === String(t.id) ? '#8b5cf6' : 'rgba(100,116,139,0.2)',
              color: filterTenant === String(t.id) ? '#fff' : '#94a3b8',
              fontWeight: filterTenant === String(t.id) ? 700 : 400,
            }}>{t.tenant_name}</button>
          ))}
          <span style={{ fontSize: 11, color: '#475569', marginLeft: 6 }}>
            {visibleExtensions.length} extension{visibleExtensions.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      <div className="content-card">
        <h3>Create Extension</h3>

        <div className="admin-form-row">
          {showTenantSelector && tenants.length > 0 && (
            <select
              className="admin-select"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              title="Assign to tenant"
            >
              {tenants.map((t: any) => (
                <option key={t.id} value={t.id}>{t.tenant_name}</option>
              ))}
            </select>
          )}

          <input
            className="admin-input"
            value={extensionNumber}
            onChange={(e) => setExtensionNumber(e.target.value)}
            placeholder="Extension e.g. 1001"
          />

          <input
            className="admin-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Display name"
          />

          <select
            className="admin-select"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            <option value="">No user assigned</option>
            {users
              .filter((u: any) => !showTenantSelector || !tenantId || String(u.tenant_id) === tenantId)
              .map((u: any) => (
                <option key={u.id} value={u.id}>{u.username}</option>
              ))}
          </select>

          <button className="primary-btn" onClick={createExtension}>
            Create Extension
          </button>
        </div>
      </div>

      <div className="content-card">
        <h3>Extension List</h3>

        <table className="admin-table">
          <thead>
            <tr>
              <th>Extension</th>
              {showTenantSelector && <th>Tenant</th>}
              <th>Display Name</th>
              <th>User</th>
              <th>Outbound Trunk</th>
              <th>Out. Limit</th>
              <th>WebRTC</th>
              <th>Internal</th>
              <th>External</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {visibleExtensions.map((e: any) => (
              <tr key={e.id}>
                <td>
                  <strong>{e.extension_number}</strong>
                </td>

                {showTenantSelector && (
                  <td>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                      background: 'rgba(139,92,246,0.15)', color: '#c4b5fd',
                    }}>
                      {tenantName(e.tenant_id)}
                    </span>
                  </td>
                )}

                <td>
                  <input
                    className="admin-input"
                    value={e.display_name || ''}
                    onChange={(ev) =>
                      updateExtension(e, { display_name: ev.target.value })
                    }
                  />
                </td>

                <td>
                  <select
                    className="admin-select"
                    value={e.user_id || ''}
                    onChange={(ev) =>
                      updateExtension(e, {
                        user_id: ev.target.value ? Number(ev.target.value) : null
                      })
                    }
                  >
                    <option value="">Unassigned</option>
                    {users.map((u: any) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                  </select>
                </td>

                <td>
                  <select
                    className="admin-select"
                    value={e.outbound_trunk_id || ''}
                    onChange={(ev) =>
                      updateExtension(e, {
                        outbound_trunk_id: ev.target.value ? Number(ev.target.value) : null
                      })
                    }
                    title="Outbound trunk for this extension (overrides tenant default)"
                  >
                    <option value="">— tenant default —</option>
                    {trunks.map((t: any) => (
                      <option key={t.id} value={t.id}>
                        {t.display_name || t.trunk_name}
                      </option>
                    ))}
                  </select>
                </td>

                <td>
                  <input
                    className="admin-input small"
                    type="number"
                    min={0}
                    style={{ width: 80 }}
                    placeholder="—"
                    title="Outbound minutes/month cap for this user (blank = none)"
                    value={e.outbound_limit_minutes ?? ''}
                    onChange={(ev) =>
                      updateExtension(e, {
                        outbound_limit_minutes: ev.target.value === '' ? null : Number(ev.target.value)
                      })
                    }
                  />
                </td>
                <td>
                  <Toggle
                    enabled={!!e.webrtc_enabled}
                    onClick={() =>
                      updateExtension(e, {
                        webrtc_enabled: e.webrtc_enabled ? 0 : 1
                      })
                    }
                  />
                </td>

                <td>
                  <Toggle
                    enabled={!!e.internal_call_enabled}
                    onClick={() =>
                      updateExtension(e, {
                        internal_call_enabled: e.internal_call_enabled ? 0 : 1
                      })
                    }
                  />
                </td>

                <td>
                  <Toggle
                    enabled={!!e.external_call_enabled}
                    onClick={() =>
                      updateExtension(e, {
                        external_call_enabled: e.external_call_enabled ? 0 : 1
                      })
                    }
                  />
                </td>

                <td>
                  <Toggle
                    enabled={!!e.is_active}
                    onClick={() =>
                      updateExtension(e, { is_active: e.is_active ? 0 : 1 })
                    }
                  />
                </td>

                <td>
                  <button
                    className="danger-btn"
                    onClick={() => deleteExtension(e)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Toggle({ enabled, onClick }: any) {
  return (
    <button
      className={`admin-pill ${enabled ? 'green' : 'red'}`}
      onClick={onClick}
      type="button"
    >
      {enabled ? 'ON' : 'OFF'}
    </button>
  );
}